/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import React from "react";
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  detailsModalColumnsStyles,
  detailsModalContentStyles,
  detailsModalLeftColumnStyles,
  detailsModalPaperStyles,
  detailsModalRightColumnStyles,
  drillInCloseButtonStyles,
  drillInHeaderActionsStyles,
  drillInHeaderStyles,
  drillInPaperBaseStyles,
  drillInTitleStyles,
  modalMessageStyles,
  modalSubtitleStyles,
} from "./styles";

/**
 * The two-column drill-in shell shared by the restaurant modal and the revenue-center
 * modal: figures on the left, the four sections on the right, each column scrolling
 * independently inside a fixed-height dialog.
 *
 * `message` short-circuits both columns for the loading, error and no-data states, which
 * every caller needs and none should render differently.
 *
 * `actions` sits in the header beside the close button, where a per-restaurant control such
 * as Export stays visible no matter how far either column has been scrolled.
 */
export const DrillInDialog = ({
  open,
  onClose,
  title,
  subtitle,
  testId,
  closeTestId,
  maxWidth = "lg",
  message,
  actions,
  left,
  right,
}) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth={maxWidth}
    data-testid={testId}
    PaperProps={{ sx: message ? drillInPaperBaseStyles : detailsModalPaperStyles }}
  >
    <Box sx={drillInHeaderStyles}>
      <DialogTitle
        component="div"
        sx={{ padding: 0, minWidth: 0, flex: "0 1 auto" }}
        data-testid={`${testId}-title`}
      >
        <Typography sx={drillInTitleStyles}>{title}</Typography>
        {subtitle ? <Typography sx={modalSubtitleStyles}>{subtitle}</Typography> : null}
      </DialogTitle>
      <Box sx={drillInHeaderActionsStyles}>
        {actions}
        <IconButton
          onClick={onClose}
          aria-label="Close"
          data-testid={closeTestId || `${testId}-close`}
          sx={drillInCloseButtonStyles}
        >
          <CloseIcon />
        </IconButton>
      </Box>
    </Box>

    <DialogContent sx={detailsModalContentStyles}>
      {message ? (
        <Box sx={modalMessageStyles} data-testid={`${testId}-message`}>
          {message}
        </Box>
      ) : (
        <Box sx={detailsModalColumnsStyles} data-testid={`${testId}-columns`}>
          <Box sx={detailsModalLeftColumnStyles} data-testid={`${testId}-primary`}>
            {left}
          </Box>
          <Box sx={detailsModalRightColumnStyles} data-testid={`${testId}-secondary`}>
            {right}
          </Box>
        </Box>
      )}
    </DialogContent>
  </Dialog>
);
